import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LockIcon, MailIcon, AlertCircleIcon, Eye, EyeOff, XIcon } from 'lucide-react';
interface TeacherLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const TeacherLoginModal = ({
  isOpen,
  onClose
}: TeacherLoginModalProps) => {
  const [email, setEmail] = useState('');
  const [passport, setPassport] = useState('');
  const [showPassport, setShowPassport] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const {
    login
  } = useAuth();
  const navigate = useNavigate();
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !passport) {
      setError('Please enter both email and passport');
      return;
    }
    try {
      setIsLoading(true);
      console.log('=== TEACHER LOGIN ATTEMPT (MODAL) ===');
      console.log('Email:', email);
      console.log('Calling login with type: teacher');
      // FIX: Add timeout protection (15 seconds)
      const loginPromise = login(email, passport, 'teacher');
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Login timeout - please try again')), 15000));
      await Promise.race([loginPromise, timeoutPromise]);
      console.log('Login successful, navigating to dashboard');
      // Close modal and navigate
      onClose();
      navigate('/teacher-dashboard');
    } catch (err: any) {
      console.error('=== TEACHER LOGIN ERROR ===');
      console.error('Login error:', err);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      // Handle timeout specifically (user-actionable)
      if (err.message?.includes('timeout')) {
        setError('Login is taking too long. Please check your internet connection and try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
      }
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6" data-id="element-11985">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={onClose} data-id="element-11986"></div>

      {/* Modal */}
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl relative z-10 animate-fadeIn mx-4 my-auto overflow-hidden" data-id="element-11987">
        <div className="px-8 py-8" data-id="element-11988">
          <div className="flex justify-between items-start mb-6" data-id="element-11989">
            <div className="text-center w-full" data-id="element-11990">
              <h2 className="text-2xl font-serif font-bold text-navy-900 mb-1" data-id="element-11991">
                Teacher Login
              </h2>
              <p className="text-gray-600 text-sm" data-id="element-11992">
                Sign in to access your dashboard
              </p>
            </div>
            <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-11993">
              <XIcon className="h-5 w-5" data-id="element-11994" />
            </button>
          </div>

          {error && <div className="mb-6 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-start text-sm" data-id="element-11995">
              <AlertCircleIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" data-id="element-11996" />
              <span data-id="element-11997">{error}</span>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-11998">
            <div data-id="element-11999">
              <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12000">
                Email Address
              </label>
              <div className="relative" data-id="element-12001">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12002">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-12003" />
                </div>
                <input id="modal-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isLoading} data-id="element-12004" />
              </div>
            </div>

            <div data-id="element-12005">
              <div className="flex items-center justify-between mb-1" data-id="element-12006">
                <label htmlFor="modal-passport" className="block text-sm font-medium text-gray-700" data-id="element-12007">
                  Passport
                </label>
                <Link to="/forgot-password" className="text-xs text-deep-pink hover:text-burgundy" onClick={onClose} data-id="element-12008">
                  Forgot password?
                </Link>
              </div>
              <div className="relative" data-id="element-12009">
                <input type={showPassport ? 'text' : 'password'} id="modal-passport" value={passport} onChange={e => setPassport(e.target.value)} className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="Enter your passport" required disabled={isLoading} data-id="element-12010" />
                <button type="button" onClick={() => setShowPassport(!showPassport)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none" aria-label={showPassport ? 'Hide passport' : 'Show passport'} data-id="element-12011">
                  {showPassport ? <EyeOff size={18} data-id="element-12012" /> : <Eye size={18} data-id="element-12013" />}
                </button>
              </div>
            </div>

            <div className="flex items-center" data-id="element-12014">
              <input id="modal-remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-gray-300 rounded" data-id="element-12015" />
              <label htmlFor="modal-remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-12016">
                Remember me
              </label>
            </div>

            <button type="submit" disabled={isLoading} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-md hover:shadow-lg transform active:scale-[0.98] transition-all" data-id="element-12017">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-12018">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-12019"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-12020"></path>
                  </svg>
                  Signing In...
                </> : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center pt-4 border-t border-gray-100" data-id="element-12021">
            <p className="text-sm text-gray-600" data-id="element-12022">
              Don't have an account?{' '}
              <Link to="/teacher-signup" className="text-deep-pink hover:text-burgundy font-medium" onClick={onClose} data-id="element-12023">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>;
};